import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface TopologyNode extends d3.SimulationNodeDatum {
  id: string;
  type: string;
  label: string;
  capabilities?: string[];
  metadata?: {
    avg_duration_ms?: number;
    success_rate?: number;
    avg_cost_usd?: number;
  };
}

interface TopologyEdge extends d3.SimulationLinkDatum<TopologyNode> {
  from: string;
  to: string;
  type: string;
  label: string;
  weight?: number;
  metadata?: {
    avg_duration_ms?: number;
  };
}

interface Topology {
  namespace: string;
  version: string;
  type: string;
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  sub_topologies?: any[];
}

interface TopologyGraphProps {
  topology: Topology | null;
  width?: number;
  height?: number;
}

const TopologyGraph: React.FC<TopologyGraphProps> = ({
  topology,
  width = 1200,
  height = 800
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<TopologyNode, TopologyEdge> | null>(null);

  useEffect(() => {
    if (!topology || !containerRef.current) return;

    // Clear previous visualization
    d3.select(containerRef.current).selectAll('*').remove();

    // Create SVG
    const svg = d3.select(containerRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('class', 'topology-graph')
      .style('border', '1px solid #ddd')
      .style('border-radius', '4px')
      .style('background', '#fff');

    // Add zoom behavior
    const g = svg.append('g');
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });

    svg.call(zoom);

    // Create simulation data with proper typing
    const nodes = topology.nodes.map(n => ({ ...n }));
    const edges = topology.edges.map(e => ({
      ...e,
      source: e.from,
      target: e.to
    }));

    // Force simulation
    const simulation = d3.forceSimulation<TopologyNode>(nodes)
      .force('link', d3.forceLink<TopologyNode, TopologyEdge>(edges)
        .id((d) => d.id)
        .distance((d) => 150 / (d.weight || 1)))
      .force('charge', d3.forceManyBody()
        .strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    simulationRef.current = simulation;

    // Add arrowhead marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .attr('xoverflow', 'visible')
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', '#999')
      .style('stroke', 'none');

    // Draw edges
    const links = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(edges)
      .enter()
      .append('line')
      .attr('stroke', (d) => edgeColor(d.type))
      .attr('stroke-width', (d) => Math.min(5, 1 + (d.weight || 1) / 10))
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', 'url(#arrowhead)');

    // Drag functions (defined before use)
    function dragStarted(event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>, d: TopologyNode) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>, d: TopologyNode) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragEnded(event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>, d: TopologyNode) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Draw nodes
    const nodeGroups = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(d3.drag<SVGGElement, TopologyNode>()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded));

    // Node circles
    nodeGroups.append('circle')
      .attr('r', 20)
      .attr('fill', (d) => nodeColor(d.type))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    // Node labels
    nodeGroups.append('text')
      .text((d) => d.label)
      .attr('font-size', 12)
      .attr('dx', 25)
      .attr('dy', 4)
      .attr('class', 'node-label')
      .style('pointer-events', 'none')
      .style('user-select', 'none');

    // Node tooltips
    nodeGroups.append('title')
      .text((d) => formatNodeTooltip(d));

    // Edge labels
    const edgeLabels = g.append('g')
      .attr('class', 'edge-labels')
      .selectAll('text')
      .data(edges)
      .enter()
      .append('text')
      .text((d) => d.label)
      .attr('font-size', 10)
      .attr('fill', '#666')
      .attr('class', 'edge-label')
      .style('pointer-events', 'none')
      .style('user-select', 'none');

    // Helper to extract node from source/target (can be string or node)
    const getNode = (nodeOrId: string | TopologyNode): TopologyNode | null => {
      return typeof nodeOrId === 'object' ? nodeOrId : null;
    };

    // Update positions on simulation tick
    simulation.on('tick', () => {
      links
        .attr('x1', (d) => {
          const source = getNode(d.source);
          return source?.x || 0;
        })
        .attr('y1', (d) => {
          const source = getNode(d.source);
          return source?.y || 0;
        })
        .attr('x2', (d) => {
          const target = getNode(d.target);
          return target?.x || 0;
        })
        .attr('y2', (d) => {
          const target = getNode(d.target);
          return target?.y || 0;
        });

      nodeGroups
        .attr('transform', (d) => `translate(${d.x || 0},${d.y || 0})`);

      edgeLabels
        .attr('x', (d) => {
          const source = getNode(d.source);
          const target = getNode(d.target);
          return ((source?.x || 0) + (target?.x || 0)) / 2;
        })
        .attr('y', (d) => {
          const source = getNode(d.source);
          const target = getNode(d.target);
          return ((source?.y || 0) + (target?.y || 0)) / 2;
        });
    });

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [topology, width, height]);

  // Helper functions
  const edgeColor = (type: string) => {
    const colors: Record<string, string> = {
      'depends_on': '#999',
      'triggers': '#3498db',
      'observed_trigger': '#2ecc71'
    };
    return colors[type] || '#999';
  };

  const nodeColor = (type: string) => {
    const colors: Record<string, string> = {
      'agent': '#3498db',
      'workflow': '#9b59b6',
      'concept': '#e74c3c'
    };
    return colors[type] || '#95a5a6';
  };

  const formatNodeTooltip = (node: TopologyNode) => {
    let tooltip = `${node.label}\nType: ${node.type}`;

    if (node.metadata) {
      if (node.metadata.success_rate !== undefined) {
        tooltip += `\nSuccess Rate: ${(node.metadata.success_rate * 100).toFixed(1)}%`;
      }
      if (node.metadata.avg_duration_ms !== undefined) {
        tooltip += `\nAvg Duration: ${node.metadata.avg_duration_ms.toFixed(0)}ms`;
      }
      if (node.metadata.avg_cost_usd !== undefined) {
        tooltip += `\nAvg Cost: $${node.metadata.avg_cost_usd.toFixed(4)}`;
      }
    }

    return tooltip;
  };

  if (!topology) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 border border-gray-200 rounded-lg p-8">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Topology Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Select a workflow or model configuration to visualize
          </p>
        </div>
      </div>
    );
  }

  return <div ref={containerRef} className="w-full" />;
};

export default TopologyGraph;
